// src/components/UploadTypeSelection.jsx
import React from 'react'
import { useTranslation } from 'react-i18next'

function UploadTypeSelection({ uploadType, onUploadTypeChange }) {
    const { t, locale } = useTranslation()
    const uploadTypes = [
        {
            id: 'full_update',
            title: t('Whole School Update'),
            subtitle: t('New School Year Setup'),
            description: t('Use this option when starting a new school year or setting up your school on Upstrive for the first time.'),
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                </svg>
            ),
            color: '#C2410C',
            features: [
                t('Replaces all student & staff data with your new file'),
                t('Removes unused classes'),
                t('Disables absent users'),
                t('Gives you a clean slate for the year ahead'),
            ],
            badge: t('Recommended for new school year'),
            badgeColor: 'bg-[#FFEDD5] text-[#C2410C]',
        },
        {
            id: 'add_only',
            title: t('Add New Users'),
            subtitle: t('Mid-Year Updates'),
            description: t('Use this option to add students or staff during the school year while keeping everything else the same.'),
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
            ),
            color: '#2F8946',
            features: [t('Adds only new users from your file'), t('Keeps existing accounts unchanged'), t('Creates classes if needed'), t('Safe to use anytime during the year')],
            badge: t('Recommended for mid-year updates'),
            badgeColor: 'bg-[#D7EFDF] text-[#2F8946]',
        },
    ]

    const handleTypeSelect = (typeId) => {
        onUploadTypeChange(typeId)
    }

    return (
        <div className="w-full">
            <div className="mb-[48px]">
                <h3 className="text-[31px] leading-[39px] tracking-tight  font-bold text-darkPurple mb-3">{t('Choose Your Upload Method')}</h3>
                <p className="text-darkPurple text-[14px] leading-[23px]  font-normal">
                    {t('Decide if you want a full reset for a new school year, or just add new users during the year.')}
                </p>
            </div>

            {/* Horizontal Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 ">
                {uploadTypes.map((type) => (
                    <div
                        key={type.id}
                        className={`relative group cursor-pointer transition-all duration-300 transform  ${uploadType === type.id ? '' : ''}`}
                        onClick={() => handleTypeSelect(type.id)}
                    >
                        {/* Card */}
                        <div
                            className={`relative rounded-xl pt-[30px] pl-[28px] pr-[32px] border-[3px] pb-[40px] h-full transition-all duration-300 ${
                                uploadType === type.id ? ` border-deepPurple shadow-[0_0_0_9px_#DEDCFB]` : ` border-deselectPurple`
                            }`}
                        >
                            {/* Selection indicator */}
                            {uploadType === type.id && (
                                <div className="absolute -top-[19px] left-1/2 -translate-x-1/2 w-[38px] h-[38px] bg-white border-[3px] border-[#5E639B] shadow-[0_0_0_9px_#DEDCFB] rounded-full z-20 flex items-center justify-center">
                                    <svg width="22" height="16" viewBox="0 0 26 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M21.6758 1.32318C22.052 0.867755 22.5912 0.577175 23.1785 0.513299C23.7658 0.449424 24.3549 0.617297 24.8203 0.981184C25.0488 1.15818 25.2396 1.3792 25.3812 1.63117C25.5229 1.88315 25.6126 2.16097 25.6451 2.44821C25.6776 2.73545 25.6521 3.02629 25.5703 3.30353C25.4884 3.58077 25.3519 3.8388 25.1686 4.06235L13.2619 18.6924C13.0627 18.9341 12.8154 19.1318 12.5358 19.2731C12.2562 19.4143 11.9503 19.496 11.6375 19.513C11.3246 19.53 11.0117 19.4818 10.7185 19.3716C10.4252 19.2615 10.158 19.0916 9.93376 18.8729L1.00376 10.1012L0.848597 9.93652C0.13293 9.07518 0.183596 7.80535 1.00376 7.00102C1.82393 6.19668 3.1191 6.14602 3.99626 6.84902L4.1641 7.00102L11.3524 14.031L21.7074 1.33268L21.6758 1.32318Z"
                                            fill="#5E639B"
                                        />
                                    </svg>
                                </div>
                            )}
                            <div className=" flex flex-row items-center justify-between flex-1 ">
                                {/* Badge */}
                                <div className={`flex flex-row items-center gap-[5px] px-[10px]  ${type.badgeColor} rounded-[16px]  h-[36px]`}>
                                    {/* User Icon (SVG) */}
                                    <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                                        <g clipPath="url(#clip0_240_2070)">
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M5.99294 4.73116C5.99294 3.47599 7.01046 2.45848 8.26563 2.45848C9.52079 2.45848 10.5383 3.47599 10.5383 4.73116C10.5383 5.98633 9.52079 7.00384 8.26563 7.00384C7.01046 7.00384 5.99294 5.98633 5.99294 4.73116ZM8.26563 0.943359C6.17368 0.943359 4.47782 2.63922 4.47782 4.73116C4.47782 6.82311 6.17368 8.51896 8.26563 8.51896C10.3576 8.51896 12.0534 6.82311 12.0534 4.73116C12.0534 2.63922 10.3576 0.943359 8.26563 0.943359ZM1.95262 14.832C1.95262 14.1137 2.11395 13.5931 2.3673 13.2023C2.62145 12.8104 3.00415 12.4938 3.5369 12.2439C4.63612 11.7284 6.25604 11.5492 8.26563 11.5492C10.2752 11.5492 11.8951 11.7284 12.9944 12.2439C13.5271 12.4938 13.9098 12.8104 14.1639 13.2023C14.4173 13.5931 14.5786 14.1137 14.5786 14.832C14.5786 15.2504 14.9178 15.5895 15.3362 15.5895C15.7546 15.5895 16.0938 15.2504 16.0938 14.832C16.0938 13.8785 15.8763 13.0583 15.4352 12.378C14.9949 11.699 14.3676 11.2145 13.6377 10.8722C12.2117 10.2034 10.2964 10.0341 8.26563 10.0341C6.23489 10.0341 4.31953 10.2034 2.89355 10.8722C2.16369 11.2145 1.53631 11.699 1.09603 12.378C0.654951 13.0583 0.4375 13.8785 0.4375 14.832C0.4375 15.2504 0.776671 15.5895 1.19506 15.5895C1.61345 15.5895 1.95262 15.2504 1.95262 14.832Z"
                                                fill={type.color}
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_240_2070">
                                                <rect x="0.4375" y="0.4375" width="15.6562" height="15.6562" fill="white" />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                    {/* Text */}
                                    <span className={`font-semibold text-[18px] leading-[100%] tracking-tight text-[${type.color}]`}>{type.badge}</span>
                                </div>
                                {/* Icon */}
                                <div className={`p-2 rounded-lg  bg-gray-100 text-deepPurple transition-colors duration-300`}>{type.icon}</div>
                            </div>

                            {/* Content */}
                            <div className="mt-[22px]">
                                <div>
                                    <h4 className="text-[22px] leading-[28px] font-bold text-darkPurple mb-1">{type.title}</h4>
                                    {/* <p className={`text-[18px] leading-5 text-darkPurple font-medium ${uploadType === type.id ? `text-${type.color}-700` : 'text-gray-500'}`}>
                                        {type.subtitle}
                                    </p> */}
                                </div>

                                <p className="text-darkPurple text-[16px] leading-5 font-normal mt-[31px] ">{type.description}</p>

                                {/* Features list */}
                                <div className="space-y-2 mt-[19px]">
                                    {type.features.map((feature, index) => (
                                        <div key={index} className="flex items-center text-sm">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path
                                                    d="M12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"
                                                    fill="#2F8946"
                                                />
                                            </svg>

                                            <span className="ml-3 text-[16px] leading-5 text-darkPurple font-semibold">{feature}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Selection prompt */}
                            <div className="mt-[21px] pt-[29px] border-t border-lightBG">
                                <p
                                    className={`text-right text-[20px] font-bold leading-5 transition-colors duration-300 ${
                                        uploadType === type.id ? `text-deepPurple ` : 'text-mudPurple group-hover:text-deepPurple'
                                    }`}
                                >
                                    {uploadType === type.id ? t('Selected') : t('Click to select')}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Help Section - Compact */}
            <div className="bg-[#F8F8FB] rounded-[16px] p-5  mt-[42px]">
                <div className="flex items-start space-x-4">
                    <div className="flex-1">
                        <div className="flex flex-row items-center gap-3">
                            <h4 className="text-[24px] font-bold leading-6 text-darkPurple tracking-tight ">{t('Quick Decision Guide')}</h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                            <div className="space-y-3">
                                <p className="font-bold text-[16px] leading-5 text-darkPurple">{t('Choose Whole School Update when')}:</p>

                                <ul className="text-[16px] leading-[18px] font-medium text-deepPurple space-y-3 ml-[10px]">
                                    <li>• {t('Starting a new academic year')}</li>
                                    <li>• {t('You have complete, current data')}</li>
                                    <li>• {t('You want to remove inactive accounts')}</li>
                                </ul>
                            </div>
                            <div className="space-y-3">
                                <p className="font-bold text-[16px] leading-5 text-darkPurple">{t('Choose Add New Users when')}:</p>

                                <ul className="text-[16px] leading-[18px] font-medium text-deepPurple space-y-3 ml-[10px]">
                                    <li>• {t('Adding students during the year')}</li>
                                    <li>• {t('Your file only has new users')}</li>
                                    <li>• {t('You want to keep existing accounts')}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Warning for Full Update */}
            {uploadType === 'full_update' && (
                <div className="mt-[42px] bg-[#FDDF6D66] border border-highlightColor rounded-[24px] p-3">
                    <div className="flex items-start space-x-3">
                        <div className="w-[50px] h-[50px] rounded-full border border-[#585695] flex items-center justify-center">
                            <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clip-path="url(#clip0_205_2723)">
                                    <path
                                        d="M1.5 3.99959V9.99959M1.5 9.99959H7.5M1.5 9.99959L6.14 5.63959C7.21475 4.5643 8.54437 3.77879 10.0048 3.35636C11.4652 2.93393 13.0089 2.88834 14.4917 3.22385C15.9745 3.55935 17.3482 4.26502 18.4845 5.27501C19.6209 6.28499 20.4828 7.56637 20.99 8.99959M23.5 19.9996V13.9996M23.5 13.9996H17.5M23.5 13.9996L18.86 18.3596C17.7853 19.4349 16.4556 20.2204 14.9952 20.6428C13.5348 21.0652 11.9911 21.1108 10.5083 20.7753C9.02547 20.4398 7.6518 19.7342 6.51547 18.7242C5.37913 17.7142 4.51717 16.4328 4.01 14.9996"
                                        stroke="#585695"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_205_2723">
                                        <rect width="24" height="24" fill="white" transform="translate(0.5)" />
                                    </clipPath>
                                </defs>
                            </svg>
                        </div>

                        <div className="space-y-3">
                            <h5 className="font-bold text-[16px] leading-5 text-darkPurple ">{t('Important: Data Replacement Warning')}</h5>
                            <p className="text-[16px] leading-[18px] font-medium text-deepPurple tracking-tight">
                                {t(
                                    'Updating will remove any accounts or classes not in your file. Double-check your file includes all current students and staff before continuing.'
                                )}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Success message for Add Only */}
            {uploadType === 'add_only' && (
                <div className="mt-[42px] bg-[#D7EFDF] border border-[#2F8946] rounded-[24px] p-3">
                    <div className="flex items-start space-x-3">
                        <div className="w-[50px] h-[50px] rounded-full border border-[#585695] flex items-center justify-center">
                            <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clip-path="url(#clip0_205_2723)">
                                    <path
                                        d="M1.5 3.99959V9.99959M1.5 9.99959H7.5M1.5 9.99959L6.14 5.63959C7.21475 4.5643 8.54437 3.77879 10.0048 3.35636C11.4652 2.93393 13.0089 2.88834 14.4917 3.22385C15.9745 3.55935 17.3482 4.26502 18.4845 5.27501C19.6209 6.28499 20.4828 7.56637 20.99 8.99959M23.5 19.9996V13.9996M23.5 13.9996H17.5M23.5 13.9996L18.86 18.3596C17.7853 19.4349 16.4556 20.2204 14.9952 20.6428C13.5348 21.0652 11.9911 21.1108 10.5083 20.7753C9.02547 20.4398 7.6518 19.7342 6.51547 18.7242C5.37913 17.7142 4.51717 16.4328 4.01 14.9996"
                                        stroke="#585695"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_205_2723">
                                        <rect width="24" height="24" fill="white" transform="translate(0.5)" />
                                    </clipPath>
                                </defs>
                            </svg>
                        </div>
                        <div className="space-y-3">
                            <h5 className="font-bold text-[16px] leading-5 text-darkPurple ">{t('Safe Addition Mode')}</h5>
                            <p className="text-[16px] leading-[18px] font-medium text-deepPurple tracking-tight">
                                {t(
                                    'Your existing data is protected. Only new users from the Excel file will be added to the system. No existing accounts will be modified or disabled.'
                                )}
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default UploadTypeSelection
